import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useSelector } from "react-redux";
import { useI18n } from "../../hooks/useI18n";
import { useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchTenantDistributors } from "../../store/tenants/actions";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { Distributor, DistributorId } from "../../models/Product";

interface DistributorMultiSelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected?: (value: ReadonlyArray<Distributor>) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<DistributorId>;
}

export const DistributorMultiSelect: FunctionComponent<
  DistributorMultiSelectProps
> = ({
  label,
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  ...props
}) => {
  const { t } = useI18n();
  const defaultLabel = label || t("common.formFields.distributor");
  const distributors = useSelector<AppState, ReadonlyArray<Distributor>>(
    s => s.tenants.distributors
  );

  const [state, setState] = useState<ReadonlyArray<Distributor>>([]);

  useLoadIfEmpty(!distributors?.length, thunkFetchTenantDistributors);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let ids = event.target.value as Array<DistributorId>;
      let values = state.filter(x => ids.includes(x.id));

      onSelected(values ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (ids: ReadonlyArray<DistributorId>): ReactNode => {
    return state
      .filter(x => ids.includes(x.id))
      .map(x => x.name)
      .join(", ");
  };

  useEffect(() => {
    let filtered = distributors && distributors.length > 0 && distributors;

    if (filtered) {
      setState(filtered);
    }
  }, [distributors]);

  return (
    <FormControl
      style={props.style}
      variant={variant}
      className={props.className}
    >
      <InputLabel
        id="distributor-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {defaultLabel}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="distributor-multi-select-label"
        id="distributor-multi-select"
        autoWidth={true}
        input={<Input />}
        renderValue={value => renderNames(value as DistributorId[])}
      >
        {state &&
          state.length > 0 &&
          state.map(x => (
            <MenuItem key={x.id} value={x.id}>
              <Checkbox checked={value.indexOf(x.id) > -1} />
              <ListItemText primary={x.name} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
